import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { AlertTriangle } from "lucide-react";
import CaptureAnalysis from "../components/capture/CaptureAnalysis";
import ViewSwitcher from "../components/capture/ViewSwitcher";

// Public, no-auth view of a shared motion-capture analysis.
//
// Uses a bare axios instance (no credentials, no auth interceptor) so it works
// for a visitor with no account at all — a parent, a prospective student, a
// hitting partner. Mirrors PublicPlan, which does the same for shared plans.
const publicGet = async (token) => {
  const r = await axios.get(`/api/capture/public/${token}`);
  return r.data;
};

// Public clip URL. Range requests are served by the backend, so the player can
// scrub exactly like it can inside the app.
export const publicClipUrl = (token) => `/api/capture/public/${token}/clip`;

export default function PublicCapture() {
  const { token } = useParams();
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    publicGet(token)
      .then((d) => {
        if (alive) {
          setAnalysis(d);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (alive) {
          setError(
            e.response?.status === 404
              ? "This share link is no longer active."
              : "Could not load this analysis."
          );
          setLoading(false);
        }
      });
    return () => {
      alive = false;
    };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0B0B] text-white grid place-items-center text-sm text-[#A3A3A3]">
        Loading…
      </div>
    );
  }

  if (error || !analysis) {
    return (
      <div
        className="min-h-screen bg-[#0B0B0B] text-white grid place-items-center p-6"
        data-testid="public-capture-inactive"
      >
        <div className="card-surface p-10 text-center max-w-md">
          <div className="w-14 h-14 mx-auto border border-amber-400/50 grid place-items-center mb-4">
            <AlertTriangle size={20} className="text-amber-400" />
          </div>
          <div className="font-display text-2xl font-black uppercase tracking-tight">Link not active</div>
          <p className="text-sm text-[#A3A3A3] mt-2">
            {error || "Ask your coach for a fresh link."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white" data-testid="public-capture-page">
      <header className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between gap-4">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#38BDF8]">
            Motion capture analysis
          </div>
          <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">
            {analysis.coach_name ? `Shared by ${analysis.coach_name}` : "Shared analysis"}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* A shared analysis can hold more than one camera angle. Each angle has
            its own token, so the switcher navigates by token and never by an
            internal session id. */}
        <ViewSwitcher
          views={(analysis.views || []).map((v) => ({ ...v, id: v.share_token }))}
          linkFor={(t) => `/c/${t}`}
          testIdPrefix="public-view"
        />

        <CaptureAnalysis
          analysis={analysis}
          // Only wire up the clip when one was actually uploaded — otherwise the
          // <video> renders an empty black box on the shared page.
          clipSrc={analysis.has_clip ? publicClipUrl(token) : null}
          clipDownloadName={`pbcoach-analysis-${(analysis.student_name || "athlete").replace(/\s+/g, "-").toLowerCase()}.mp4`}
          footnote="Shared view — read only."
        />
      </main>
    </div>
  );
}
